import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { normaliseLevel, normaliseSubject } from "@/lib/searchUtils";

export interface CourseInfo {
  subject: string;
  education_level: string;
  exam_boards: string[];
  note_count: number;
  course_code: string;
  /** Raw (un-normalised) education_level value from the DB. Needed for
   * exact-match filtering when navigating into a course page, since some
   * rows have non-canonical level strings (e.g. "Edexcel Igcse", "igsce"). */
  raw_education_level: string;
  school: string | null;
}

export function useCourses(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["courses"],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("browsable_notes")
        .select("course_code, education_level, subject, school, exam_board")
        .eq("status", "approved");
      if (error) throw error;

      const courseMap = new Map<string, CourseInfo>();
      (data || []).forEach((note: any) => {
        const normSubject = normaliseSubject(note.subject || "");
        const normLevel = normaliseLevel(note.education_level || "");
        const courseCode = (note.course_code || "General").trim();
        // Group by course_code as well so each unique course gets its own
        // card. Previously courses with multiple course_codes per
        // (subject, level) collapsed into one card whose count overstated
        // what the linked page would actually show.
        const key = `${normSubject}::${normLevel}::${courseCode.toLowerCase()}`;

        if (courseMap.has(key)) {
          const existing = courseMap.get(key)!;
          existing.note_count++;
          if (note.exam_board && !existing.exam_boards.includes(note.exam_board)) {
            existing.exam_boards.push(note.exam_board);
          }
        } else {
          courseMap.set(key, {
            subject: normSubject,
            education_level: normLevel,
            course_code: courseCode,
            raw_education_level: note.education_level || "",
            school: note.school,
            exam_boards: note.exam_board ? [note.exam_board] : [],
            note_count: 1,
          });
        }
      });

      return Array.from(courseMap.values()).sort((a, b) => b.note_count - a.note_count);
    },
  });
}
